import React, { useState, useEffect } from 'react';
import { Drawer, Box, Button, TextField, Typography, Grid, Stack, Tooltip, IconButton, Divider } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import ReactQuill from 'react-quill';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import 'react-quill/dist/quill.snow.css';
import { toast } from 'react-toastify';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { addQuarterGoal, updateQuarterGoal } from '../../services/companyQuarterGoalsServices';
import { FilterSectionComponent } from './FilterSectionComponent';

const modules = {
  toolbar: [
    [{ header: [1, 2, 3, 4, 5, 6, false] }],
    ['bold', 'italic', 'underline', 'blockquote'],
    [{ size: ['small', false, 'large', 'huge'] }], // Font size options
    [{ font: [] }], // Font family options
    [{ align: [] }], // Text alignment options
    [{ list: 'ordered' }, { list: 'bullet' }], // Ordered and unordered lists
    [{ color: [] }, { background: [] }], // Text and background color options
    ['link'], // Link option
  ],
};

const formats = [
  'header',
  'bold',
  'italic',
  'underline',
  'blockquote',
  'list',
  'bullet',
  'align',
  'link',
  'image',
  'color',
  'background',
  'font',
  'size',
];

const QuarterGoalDrawer = ({ open, onClose, goal, isEditing, onSave, classes, isAdminFlag }) => {
  const [isLoading, setIsLoading] = useState(false);

  const validationSchema = Yup.object({
    title: Yup.string().required('Title is required'),
    description: Yup.string().required('Description is required'),
    year: Yup.number().required('Year is required'),
    quarter: Yup.string().required('Quarter is required'),
  });

  const formik = useFormik({
    initialValues: {
      title: goal?.title || '',
      description: goal?.description || '',
      year: goal?.year || new Date().getFullYear(),
      quarter: goal?.quarter || 'Q1',
    },
    validationSchema,
    onSubmit: async (values) => {
      setIsLoading(true);
      try {
        if (isEditing) {
          await updateQuarterGoal(goal.id, values);
          toast.success('Goal updated successfully');
        } else {
          await addQuarterGoal(values);
          toast.success('Goal added successfully');
        }
        onSave();
        onClose();
      } catch (error) {
        toast.error(error?.response?.data?.message || 'Failed to save goal');
        console.error(error);
      } finally {
        setIsLoading(false);
      }
    },
  });

  useEffect(() => {
    if (goal) {
      formik.setValues({
        title: goal.title,
        description: goal.description,
        year: goal.year,
        quarter: goal.quarter,
      });
    }
  }, [goal]);

  // Only trigger formik.handleBlur when necessary, not on paste
  const handleQuillChange = (value) => {
    formik.setFieldValue('description', value);
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { width: '60%' },
      }}
    >
      <Box p={2}>
        <Stack direction="row" sx={{ justifyContent: 'space-between' }}>
          <Typography
            variant="h5"
            align="center"
            sx={{
              m: '8px 0',
              color: '#999',
              fontWeight: 600,
              fontSize: { xs: '.9rem', sm: '1rem', md: '1.25rem' },
              paddingLeft: '24px',
            }}
          >
            {isEditing ? 'Edit Quarter Goal' : 'Add Quarter Goal'}
          </Typography>
          <Tooltip title="back">
            <IconButton aria-label="expand row" size="small" onClick={onClose}>
              <ArrowForwardIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
        <Divider sx={{ mb: 1 }} />
        <Box m={3} gap={2}>
          <Grid item xs={12} mb={1}>
            <TextField
              label="Title"
              name="title"
              value={formik.values.title}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.title && Boolean(formik.errors.title)}
              helperText={formik.touched.title && formik.errors.title}
              fullWidth
              variant="outlined"
            />
          </Grid>

          <Grid item xs={12} className={classes.quillGrid}>
            <ReactQuill
              theme="snow"
              name="description"
              value={formik.values.description}
              onChange={handleQuillChange} // onChange updates description field
              modules={modules}
              formats={formats}
              placeholder="Add task description..."
            />
            {formik.touched.description && formik.errors.description && (
              <div style={{ color: 'red', marginLeft: '14px', fontSize: '.75rem' }}>{formik.errors.description}</div>
            )}
          </Grid>

          <FilterSectionComponent
            selectedYear={formik.values.year}
            selectedQuarter={formik.values.quarter}
            handleYearChange={(event, newValue) => formik.setFieldValue('year', newValue)}
            handleQuarterChange={(event, newValue) => formik.setFieldValue('quarter', newValue.value)}
            isAdminFlag={isAdminFlag}
          />

          <>
            <Typography variant="h6" mt={2}>
              Current Data Preview
            </Typography>
            <Box className={classes.goalBox}>
              <Typography variant="h5">{formik.values.title}</Typography>
              <Typography
                className={classes.description}
                variant="body1"
                dangerouslySetInnerHTML={{ __html: formik.values.description }}
              />
            </Box>
          </>

          <Box mt={2} display="flex" justifyContent="flex-end">
            <Button onClick={onClose} color="primary">
              Cancel
            </Button>
            <LoadingButton loading={isLoading} onClick={formik.handleSubmit} color="primary" variant="contained">
              {isEditing ? 'Update' : 'Add'}
            </LoadingButton>
          </Box>
        </Box>
      </Box>
    </Drawer>
  );
};

export default QuarterGoalDrawer;
